import { QueryClient } from '@tanstack/react-query';

// One client for the whole app. Exported as a module singleton so imperative
// helpers outside React (bumpConversation, deleteConversation, …) can read and
// write the same cache the hooks render from.
//
// `navigator.onLine` is not "can I reach the network" — it is "does the OS report an interface with a
// route", and a VPN client raising or dropping one flips it while every socket keeps working. React
// Query's default `networkMode: 'online'` PAUSES on that flag: no request, no error, `status` stuck at
// 'loading' forever, and nothing logged. One lying flag then freezes the entire data layer behind
// skeletons that never resolve. This app has no offline mode, so a query is always better off attempting
// and failing visibly — `retry: false` turns a real network failure into an error state with a retry.
const networkMode = 'always';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 30_000,
    },
    mutations: {
      networkMode,
    },
  },
});

export const queryKeys = {
  chatsAll: ['chats-v1'] as const,
  chats: (owner: string | null, query: string, channel: string) => ['chats-v1', owner, query, channel] as const,
  chatWindows: ['chat-windows-v1'] as const,
  chatWindow: (owner: string | null, id: string, cursor?: string) =>
    ['chat-windows-v1', owner, id, cursor ?? null] as const,
  conversations: ['conversations'] as const,
  // The account's region (Site Properties `locale.country`) — numeric date order follows it rather
  // than the browser's locale. See `lib/date-locale.ts`.
  siteCountry: ['site-country'] as const,
  // The account's business name (Site Properties `businessName`) — the Compliance page's header.
  siteBusinessName: ['site-business-name'] as const,
  // /api/compliance/posture — the account's HIPAA posture (lib/compliance.ts). One key: the checklist,
  // the sections and the Settings row entry all describe the same read.
  compliancePosture: ['compliance-posture'] as const,
  // Wix's BAA text, read only when the signing panel opens — not page-load data.
  complianceBaaDocument: ['compliance-baa-document'] as const,
  // Customer data requests (docs/compliance/customer-data-requests.md §3) — read only while the Manage data
  // modal is open: the account's sites, and the requests read live from Wix's privacy platform per site.
  complianceDataRequestSites: ['compliance-data-request-sites'] as const,
  complianceDataRequests: ['compliance-data-requests'] as const,
  // /api/wins — the Wins page, newest first, one entry per page of the cursor walk.
  wins: ['wins'] as const,
  brief: ['brief'] as const,
  // The spoken brief's text, split into sentences — fetched only when the user opens the read-along, and
  // keyed on the brief so a cached script can never outlive the audio it is meant to track.
  briefScript: (conversationId: string | undefined) => ['brief-script', conversationId] as const,
  search: (query: string) => ['search', query] as const,
  contacts: (query: string) => ['contacts', query] as const,
  // The agent's own people store, behind the Contacts screen — a different book from `contacts`
  // above, which is the chat composer's Wix Contacts picker. Keyed on the query because a search is
  // a different question from a browse, not a filter over the same answer.
  contactBook: (query: string) => ['contact-book', query] as const,
  // The WHOLE people book, a page at a time, behind the Outreach Contacts tab. Deliberately NOT under
  // the `['contact-book']` prefix: a send invalidates that prefix to re-badge the rows, and the book's
  // rows carry no threads to re-badge — under it, every send would re-walk every page scrolled so far.
  contactBookWhole: ['contact-book-whole'] as const,
  contactBookPerson: (personId: string | undefined) => ['contact-book-person', personId ?? ''] as const,
  // A contact the people book knows only by address has no person id to key on; it reads by its
  // recipient id instead. Separate key so the two never collide in the cache.
  contactBookRecipient: (recipientId: string | undefined) =>
    ['contact-book-recipient', recipientId ?? ''] as const,
  places: (query: string, language?: string) => ['places', query, language ?? ''] as const,
  workspaces: ['workspaces'] as const,
  // `undefined` workspaceId = the account-wide list. Every entry shares `tasksAllScopes` so a task
  // mutation can patch each cached scope at once via setQueriesData.
  tasks: (workspaceId?: string) => ['tasks', workspaceId] as const,
  tasksAllScopes: ['tasks'] as const,
  /** The Maestro's in-flight NON-task work (a Wixel render, a free-run loop) — the island's action rows.
   *  Never fetched on its own: populated by every account-wide `fetchTasks` from the same response the
   *  rows rode in on, so it refreshes (and empties) on exactly the cadence the tasks list does. */
  maestroActions: ['maestro-actions'] as const,
  /** Both directions of the account's outreaches, read BESIDE the task list rather than inside it — the
   *  re-entrant bridge hop that was ~half of `/api/tasks`'s median. Account-wide by nature (an Outreach
   *  has no workspace), so one entry serves every task scope; `lib/outreaches.ts` merges it in. An
   *  ABSENT entry means "never read", never "there are none" — nothing writes it but a successful read. */
  outreaches: ['outreaches'] as const,
  /** The account-wide activity log (AgentAction), paged by cursor via useInfiniteQuery. */
  activity: ['activity'] as const,
  /** The map home's opening reel — the last 24h of the AgentAction ledger, newest-first, capped. Its own
   *  key rather than `activity`'s pages: a different window, a different limit, and no cursor. */
  homeMapReplay: ['home-map-replay'] as const,
  homeWorldRecap: (until: number | null) => ['home-world-recap', until] as const,
  /** The map home's MCP servers read (the Connectors & MCP page's list, drawn as tools under the ring). */
  homeMapMcp: ['home-map-mcp'] as const,
  /** The first page of notifications, read for the map's timeline (its own key: the feed's is an infinite query). */
  homeMapNotifications: ['home-map-notifications'] as const,
  /** The Wix site behind the account (display name), for the map's Wix card. */
  siteInfo: ['site-info'] as const,
  /** The account's outreach preferences: the outbound email sender and the wanted channels. */
  outreachPreferences: ['outreach-preferences'] as const,
  /** Is the Maestro mid-turn right now? Polled only while the chat is closed; never persisted. */
  maestroStatus: ['maestro-status'] as const,
  /**
   * ONE task, read on its own. The three per-task entries below are keyed by TASK, not by scope, so the
   * agent-task push invalidates the one task it names rather than the whole family — see
   * `lib/agent-task-invalidation.ts` for why the prefix sweep that serves the LIST keys would be a
   * refetch storm here (one entry per task on screen, each its own request).
   *
   * They used to be inline arrays at their call sites, which is exactly how they ended up outside the
   * shared invalidation: a key the registry cannot see is a key no sweep reaches, so the detail modal
   * had no push at all and the in-chat at-work card kept its own private subscription.
   */
  taskDetail: (taskId: string) => ['task-detail', taskId] as const,
  /** The in-chat "agent at work" card's own read — the two conversations, their rows, and the task's
   *  live title/status/liveness. */
  taskAtWork: (taskId: string) => ['task-at-work', taskId] as const,
  /** One task's agent-conversation transcript, for the panel that renders it. */
  taskAgentConversation: (taskId: string) => ['task-agent-conversation', taskId] as const,
  /** One task's liveness verdict, polled by the task-detail surfaces. Has no duplexer subscription of
   *  its own, so without it in the shared set a cancel/stop left the detail modal's live marker up for
   *  the whole of its ~20s poll. */
  taskLiveness: (taskId: string) => ['task-liveness', taskId] as const,
  workspaceTasks: (workspaceId: string | undefined) => ['workspace-tasks', workspaceId] as const,
  // Every workspace's task entry, as one prefix. The agent-task push invalidates this whole family in a
  // single sweep (see `lib/agent-task-invalidation.ts`) rather than one refetch per mounted reader.
  workspaceTasksAllScopes: ['workspace-tasks'] as const,
  workspaceGoals: (workspaceId: string | undefined) => ['workspace-goals', workspaceId] as const,
  // One goal's ledger — its decisions and its plan changes. Keyed by the goal (and, for a member, its
  // parent, because that is what the read is scoped by), so two panels never share a cache entry.
  goalLedger: (goalId: string | undefined, parentGoalId: string | undefined) => ['goal-ledger', goalId, parentGoalId ?? ''] as const,
  /** The OPEN questions raised in one conversation — the brief's own answerable rows. */
  // The closed-approvals variant is its own entry, so a brief card and an approval thread never share a
  // payload of a different shape. Both sit under the `conversation-decisions` prefix a verdict invalidates.
  conversationDecisions: (conversationId: string | undefined, includeClosedMeeting = false, includeClosedProposals = false) =>
    [
      'conversation-decisions',
      conversationId,
      ...(includeClosedMeeting ? ['closedMeeting'] : []),
      ...(includeClosedProposals ? ['closedProposals'] : []),
    ] as readonly unknown[],
  opportunities: ['opportunities'] as const,
  workspaceFiles: (workspaceId: string | undefined) => ['workspace-files', workspaceId] as const,
  // ONE file resolved by a chat attachment's vfsPath (`/api/workspace-file`) — the scoped fallback when
  // the whole-file-system resolution above comes back empty. Keyed on the vfsPath, so each chip's fallback
  // is its own cache entry.
  workspaceFile: (vfsPath: string | undefined) => ['workspace-file', vfsPath] as const,
  workspaceFileRecord: (vfsPath: string | undefined) => ['workspace-file-record', vfsPath] as const,
  // Every Space's files in ONE request (`useAllWorkspaceFiles`) — shared by the cross-workspace
  // `/files` browser and the Files summary tiles. It gets its OWN key rather than reusing
  // `workspaceFiles(undefined)`: a workspace-scoped caller passes `undefined` while its slug is still
  // resolving, and although its query is disabled there, React Query would still serve it whatever sits
  // under that key — which would flash EVERY Space's files inside one Space's gallery.
  allWorkspaceFiles: ['workspace-files', 'all-spaces'] as const,
  /** One Space's mini-apps (`/api/workspace-apps`) — the Apps section above its Files browser. */
  workspaceApps: (workspaceId: string | undefined) => ['workspace-apps', workspaceId] as const,
  /** Who can open ONE mini-app. Keyed by app so a revoke invalidates only that app's list. */
  appShares: (appId: string | undefined) => ['app-shares', appId] as const,
  /** The owner's own connections + their tools, for the share dialog's lending picker. */
  lendableConnections: ['lendable-connections'] as const,
  sharedApps: () => ['shared-apps'] as const,
  // Who can read one of YOUR files. Keyed per file, because the share dialog opens on one.
  fileShares: (fileId: string | undefined) => ['file-shares', fileId] as const,
  // What OTHER people have shared with you — one list for the whole account, accepted and pending.
  sharedFiles: () => ['shared-files'] as const,
  /** One Office file's server-side markdown conversion, keyed by its URL — deterministic, so it is
   *  fetched once per session and shared by the thumbnail and the viewer. */
  convertedDocument: (url: string) => ['converted-document', url] as const,
  // The files one task produced, listed off the file-system by origin. Keyed by task.
  taskFiles: (taskId: string | undefined) => ['task-files', taskId] as const,
  // The per-chat Files panel's list, keyed by conversation.
  /** ONE conversation's row, for a chat the sidebar list page does not hold. */
  conversation: (conversationId: string | undefined) => ['conversation', conversationId] as const,
  conversationAttachments: (conversationId: string | undefined) => ['conversation-attachments', conversationId] as const,
  workspaceRoutines: (workspaceId: string | undefined) => ['workspace-routines', workspaceId] as const,
  myAgents: ['my-agents'] as const,
  suggestedTasks: ['suggested-tasks'] as const,
  pulses: ['pulses'] as const,
  // The ENRICHED routines list (`/api/routines`). `undefined` workspaceId = the account-wide list.
  // Every entry shares `routinesAllScopes` so one optimistic edit reaches whichever scopes are cached.
  routines: (workspaceId?: string) => ['routines', workspaceId] as const,
  routinesAllScopes: ['routines'] as const,
  packs: ['packs'] as const,
  pack: (slug: string) => ['pack', slug] as const,
  // Every cached pack detail — the sender profile is global, so a profile save touches them all.
  packAllDetails: ['pack'] as const,
  notifications: ['notifications'] as const,
  // /api/whats-new for one locale — public product copy, the Notifications screen's second tab.
  whatsNew: (locale: string) => ['whats-new', locale] as const,
  // /api/flags/whats-new — `symphonyWhatsNew`, whether that tab exists at all.
  whatsNewFlag: ['whats-new-flag'] as const,
  agentModels: ['agent-models'] as const,
  // /api/connectors/status — the in-chat connector card derives its live state from this
  // (poll-while-waiting + focus refetch). One key so every card shares one read.
  connectors: ['connectors'] as const,
  // Alfred's onboarding state (number + forwarding codes). Read fresh on every open: it is what decides
  // whether the next tap buys a number.
  alfredSetup: ['alfred-setup'] as const,
  // Alfred's settings screen (`/alfred`): status, number, forwarding codes and how it sounds. Read-only.
  alfredSettings: ['alfred-settings'] as const,
  // ONE cache entry for /api/credits/balance, shared by the sidebar chip and the settings cards —
  // per-instance snapshots are how the two showed different balances at the same moment.
  creditsBalance: ['credits-balance'] as const,
  // /api/credits/movements — the Credit usage statement, one infinite query per filter tab.
  creditMovements: ['credit-movements'] as const,
  referrals: ['referrals'] as const,
  // /api/reward-rules — the served social/referral reward amounts, the official handles, and the
  // networks this account has already claimed. One key, shared by both share layouts.
  rewardRules: ['reward-rules'] as const,
  voicePicker: ['voice-picker'] as const,
  geoCountry: ['geo-country'] as const,
  mobileAppStatus: ['mobile-app-status'] as const,
  // A `.md`/`.html`/`.csv`/text document body fetched for the in-app doc viewer, keyed by its (signed) URL.
  docText: (url: string) => ['doc-text', url] as const,
  // A PDF's BYTES, as a Blob. Separate from `docText` because the payload is binary and the cap differs;
  // caching the Blob (not an object URL) is what makes re-opening the same PDF instant with no refetch.
  docBlob: (url: string) => ['doc-blob', url] as const,
  // An Office document converted server-side to CSV/markdown. Keyed by kind as well as URL: the same URL
  // can only ever be one kind, but keeping it in the key makes a cache entry self-describing.
  docConvert: (url: string, kind: string) => ['doc-convert', kind, url] as const,
  // The owner's Symphony profile links (lib/profile-links-api.ts): the list, and one link's leads.
  profileLinksGate: ['profile-links-gate'] as const,
  profileLinks: ['profile-links'] as const,
  profileLinkLeads: (linkId: string) => ['profile-links', 'leads', linkId] as const,
};
